import {
  getMembers,
  simulateRules,
  RuleSimulationApiError,
  type MemberListItemContract,
  type RuleSimulationResponse,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalCard,
  CanonicalDefinitionList,
  CanonicalNotice,
  CanonicalPageHeader,
  EmptyState,
  FormActions,
  SearchSelectField,
  StatePanel,
  StatusBadge,
  TextField,
  canonicalStates,
} from '@urbanik/ui';
import { Calculator, CheckCircle2, FileText, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import './rule-simulator.css';
const money = new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' });
const periodLabels = {
  once: 'Una vez',
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
} as const;
export function RuleSimulatorView() {
  const [members, setMembers] = useState<readonly MemberListItemContract[]>([]),
    [loadingMembers, setLoadingMembers] = useState(true),
    [memberQuery, setMemberQuery] = useState(''),
    [selectedMember, setSelectedMember] = useState<MemberListItemContract | null>(null),
    [memberId, setMemberId] = useState(''),
    [period, setPeriod] = useState(''),
    [running, setRunning] = useState(false),
    [result, setResult] = useState<RuleSimulationResponse | null>(null),
    [notice, setNotice] = useState<{ message: string; tone: 'error' | 'success' } | null>(null),
    [forbidden, setForbidden] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoadingMembers(true);
    getMembers(
      apiBase,
      {
        query: memberQuery,
        status: 'active',
        sort: 'name',
        direction: 'asc',
        page: 1,
        pageSize: 20,
      },
      controller.signal,
    )
      .then((x) => setMembers(x.items))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setNotice({
          message: error instanceof Error ? error.message : 'No pudimos consultar socios.',
          tone: 'error',
        });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingMembers(false);
      });
    return () => controller.abort();
  }, [memberQuery]);
  const memberOptions = [
    ...(selectedMember && !members.some((member) => member.id === selectedMember.id)
      ? [selectedMember]
      : []),
    ...members,
  ].map((member) => ({
    value: member.id,
    label: `${member.familyNames} ${member.givenNames}`,
    meta: `Cédula ${member.nationalId}${member.slotNumber ? ` · Cupo ${member.slotNumber}` : ''}`,
  }));
  const run = async () => {
    if (!memberId || !period) {
      setNotice({ message: 'Selecciona un socio y un período.', tone: 'error' });
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const value = await simulateRules(apiBase, { memberId, period });
      setResult(value);
      setNotice({ message: 'Simulación completada sin crear obligaciones.', tone: 'success' });
    } catch (error) {
      if (error instanceof RuleSimulationApiError && error.kind === 'forbidden') setForbidden(true);
      else
        setNotice({
          message: error instanceof Error ? error.message : 'No pudimos simular las reglas.',
          tone: 'error',
        });
    } finally {
      setRunning(false);
    }
  };
  if (forbidden) return <StatePanel state={canonicalStates.find((x) => x.kind === 'forbidden')!} />;
  return (
    <div className="rule-simulator-page">
      <CanonicalNotice
        message={notice?.message ?? ''}
        tone={notice?.tone ?? 'error'}
        onDismiss={() => setNotice(null)}
      />
      <CanonicalPageHeader
        title="Simulador acumulado de obligaciones"
        description="Calcula todas las cuotas exigibles hasta el período consultado, por cada alícuota. El resultado es informativo y no crea obligaciones."
      />
      <CanonicalCard title="Datos de la simulación" icon={<Calculator aria-hidden="true" />}>
        <div className="rule-simulator-form">
          <SearchSelectField
            required
            label="Socio"
            hint="Busca por nombre, cédula o número de cupo y selecciona un resultado."
            placeholder="Buscar socio…"
            emptyMessage="No hay socios activos que coincidan con la búsqueda."
            loading={loadingMembers}
            value={memberId}
            onQueryChange={setMemberQuery}
            onValueChange={(nextId) => {
              setMemberId(nextId);
              setSelectedMember(members.find((member) => member.id === nextId) ?? null);
            }}
            options={memberOptions}
          />
          <TextField
            required
            label="Período"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.currentTarget.value)}
          />
          <FormActions>
            <Button
              variant="primary"
              icon={<Calculator />}
              disabled={running}
              onClick={() => void run()}
            >
              {running ? 'Simulando…' : 'Simular aplicación'}
            </Button>
          </FormActions>
        </div>
      </CanonicalCard>
      {running ? <StatePanel state={canonicalStates.find((x) => x.kind === 'loading')!} /> : null}
      {result ? (
        <section className="rule-simulation-result" aria-label="Desglose de la simulación">
          <CanonicalDefinitionList
            columns={4}
            label="Resumen"
            items={[
              { label: 'Socio', value: `${result.member.familyNames} ${result.member.givenNames}` },
              { label: 'Período', value: result.period },
              { label: 'Conceptos con saldo', value: result.applicableCount },
              { label: 'Total informativo', value: money.format(Number(result.totalAmount)) },
            ]}
          />
          <div className="rule-simulation-disclaimer">
            <ShieldCheck aria-hidden="true" />
            <p>{result.disclaimer}</p>
          </div>
          {result.items.length === 0 ? (
            <EmptyState
              title="No existen reglas para evaluar"
              description="No hay versiones vigentes registradas en el catálogo."
            />
          ) : (
            result.items.map((item) => (
              <CanonicalCard
                key={item.ruleId}
                className="rule-simulation-item"
                title={`${item.conceptCode} · ${item.conceptName}`}
                icon={item.applies ? <CheckCircle2 /> : <FileText />}
              >
                <div className="rule-simulation-item__status">
                  <StatusBadge tone={item.applies ? 'success' : 'neutral'}>
                    {item.applies ? 'Aplica' : 'No aplica'}
                  </StatusBadge>
                  <strong>{item.applies ? money.format(Number(item.subtotal)) : '—'}</strong>
                </div>
                <CanonicalDefinitionList
                  columns={2}
                  items={[
                    {
                      label: 'Regla y versión',
                      value: `${item.ruleCode} · v${item.versionNumber}`,
                    },
                    { label: 'Periodicidad', value: periodLabels[item.periodicity] },
                    { label: 'Explicación', value: item.explanation },
                    { label: 'Condición institucional', value: item.condition },
                    { label: 'Acta de respaldo', value: item.actNumber },
                    {
                      label: 'Valor unitario',
                      value: money.format(Number(item.unitAmount)),
                    },
                    { label: 'Cantidad de cuotas', value: item.quantity },
                    { label: 'Alícuotas consideradas', value: item.aliquotCount },
                    {
                      label: 'Períodos acumulados',
                      value:
                        item.fromPeriod && item.toPeriod
                          ? `${item.fromPeriod} a ${item.toPeriod}`
                          : 'Sin períodos exigibles',
                    },
                  ]}
                />
              </CanonicalCard>
            ))
          )}
        </section>
      ) : null}
    </div>
  );
}
